/**
 * /channel-partner — where an account stands with the Channel Partner
 * programme, rendered straight from `/me`:
 *
 *   can_access_hub            → the Hub
 *   application pending       → "Channel Partner Approval Request Pending"
 *   application rejected      → the decision; re-apply once `can_apply`
 *   invitation draft          → complete the invited application
 *   nothing yet (`can_apply`) → straight to the form (eligibility + questions)
 *
 * The ordinary dashboard stays available in every state.
 */
import type * as React from 'react';
import { Link, Navigate } from '@tanstack/react-router';
import { ArrowLeft, Handshake, Hourglass, MailPlus, XCircle, type LucideIcon } from 'lucide-react';
import { formatDate } from '@finmin/utils';
import { Button, Card, CardContent, ErrorMessage, Spinner } from '@finmin/ui';
import {
  useChannelPartnerContext,
  useChannelPartnerOptions,
  type ChannelPartnerApplication,
  type ChannelPartnerOptions,
} from '@/api/channel-partners';
import { ChannelPartnerHero } from './cp-hero';

const DATETIME = 'dd MMM yyyy, h:mm a';

export function ChannelPartnerStatusPage() {
  const me = useChannelPartnerContext();
  const options = useChannelPartnerOptions();

  if (me.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (me.error || !me.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-10 sm:px-6">
        <ErrorMessage message={me.error?.message ?? 'Could not load your Channel Partner status.'} />
        <Button variant="outline" onClick={() => void me.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const ctx = me.data;
  if (ctx.can_access_hub) return <Navigate to="/channel-partner/hub" replace />;

  const app = ctx.application;
  let view: { title: string; description: string; body: React.ReactNode };

  if (app?.status === 'pending') {
    view = {
      title: 'Channel Partner Approval Request Pending',
      description: 'Thank you for your interest. Your request is pending for the FinminOS team’s approval.',
      body: <PendingPanel application={app} options={options.data} />,
    };
  } else if (app?.status === 'rejected') {
    view = {
      title: 'Channel Partner application update',
      description: 'Our team has reviewed your application.',
      body: (
        <RejectedPanel
          application={app}
          canApply={ctx.can_apply}
          reapplyAt={ctx.reapply_allowed_at ?? app.reapply_allowed_at}
        />
      ),
    };
  } else if (app?.status === 'draft' && ctx.can_apply) {
    view = {
      title: 'You are invited to become a Channel Partner',
      description: 'Complete your application to send it for review.',
      body: <InvitedPanel />,
    };
  } else if (ctx.can_apply) {
    // Nothing submitted yet: registering starts on the form itself.
    return <Navigate to="/channel-partner/apply" replace />;
  } else {
    view = {
      title: 'Channel Partner',
      description: 'The Channel Partner programme is not available for this account right now.',
      body: (
        <Panel icon={Handshake} title="Not available right now">
          <p>
            Your account can&apos;t open the Channel Partner Hub at the moment. If you think this is a
            mistake, please contact FinminOS support.
          </p>
        </Panel>
      ),
    };
  }

  return (
    <div className="min-h-screen bg-background">
      <ChannelPartnerHero eyebrow="Channel Partner" title={view.title} description={view.description} />
      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/dashboard"
          className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" aria-hidden />
          Back to Dashboard
        </Link>
        {view.body}
      </main>
    </div>
  );
}

// ── Panels ───────────────────────────────────────────────────────────────────

function Panel({
  icon: Icon,
  title,
  tone = 'default',
  children,
  action,
}: {
  icon: LucideIcon;
  title: string;
  tone?: 'default' | 'warning' | 'destructive';
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  const toneClass = {
    default: 'bg-muted text-foreground',
    warning: 'bg-warning/15 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
  }[tone];
  return (
    <Card>
      <CardContent className="space-y-4 p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${toneClass}`}>
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1 space-y-2 text-sm text-muted-foreground">
            <h2 className="font-display text-xl font-semibold tracking-tight text-foreground">{title}</h2>
            {children}
          </div>
        </div>
        {action && <div className="flex flex-wrap gap-2 sm:pl-[60px]">{action}</div>}
      </CardContent>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  );
}

function PendingPanel({
  application,
  options,
}: {
  application: ChannelPartnerApplication;
  options: ChannelPartnerOptions | undefined;
}) {
  const typeLabel =
    options?.partner_types.find((t) => t.id === application.partner_type)?.label ??
    application.partner_type;
  const partnerType =
    application.partner_type === 'others' && application.other_partner_type
      ? `${typeLabel} — ${application.other_partner_type}`
      : typeLabel;
  return (
    <Panel
      icon={Hourglass}
      tone="warning"
      title="Thank you for your interest"
      action={
        <Button variant="outline" asChild>
          <Link to="/dashboard">Go to dashboard</Link>
        </Button>
      }
    >
      <p>
        Your request is pending for the FinminOS team’s approval. Submitted{' '}
        {formatDate(application.submitted_at, DATETIME)} — you will get a notification once it has been
        reviewed{application.cp_email ? `, and updates also go to ${application.cp_email}` : ''}. Everything
        else in your account works as usual.
      </p>
      <dl className="grid grid-cols-1 gap-3 rounded-md border border-border bg-card p-4 sm:grid-cols-2">
        <Detail label="Partner type" value={partnerType} />
        <Detail label="Organisation" value={application.organisation_name} />
        <Detail
          label="Location"
          value={[application.city, application.state].filter(Boolean).join(', ')}
        />
        <Detail label="Channel Partner email" value={application.cp_email} />
      </dl>
    </Panel>
  );
}

function RejectedPanel({
  application,
  canApply,
  reapplyAt,
}: {
  application: ChannelPartnerApplication;
  canApply: boolean;
  reapplyAt: string | null;
}) {
  return (
    <Panel
      icon={XCircle}
      tone="destructive"
      title="Your application was not approved"
      action={
        canApply ? (
          <Button asChild>
            <Link to="/channel-partner/apply">Apply again</Link>
          </Button>
        ) : (
          <Button variant="outline" asChild>
            <Link to="/dashboard">Go to dashboard</Link>
          </Button>
        )
      }
    >
      <p>Reviewed {formatDate(application.reviewed_at, DATETIME)}.</p>
      {application.rejection_reason && (
        <blockquote className="whitespace-pre-line rounded-r-md border-l-2 border-destructive/50 bg-muted px-3 py-2 text-foreground">
          {application.rejection_reason}
        </blockquote>
      )}
      <p>
        {canApply
          ? 'You can submit a new application now.'
          : reapplyAt
            ? `You can apply again from ${formatDate(reapplyAt, DATETIME)}.`
            : 'You can apply again later.'}
      </p>
    </Panel>
  );
}

function InvitedPanel() {
  return (
    <Panel
      icon={MailPlus}
      title="FinminOS has invited you"
      action={
        <Button asChild>
          <Link to="/channel-partner/apply">Complete application</Link>
        </Button>
      }
    >
      <p>
        Fill in a few details and accept the Channel Partner Agreement. The Hub opens once your
        application is approved.
      </p>
    </Panel>
  );
}
